// Recipient side, /r/:token. No login.
// Steps: receive -> (verify, mobile number + TrustStamp text + polling) ->
// recipient or proxy (proxy can invite the recipient) -> template form -> submitted.
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { Input } from '@/components/form/fields/Input/Input';
import { PhoneInput } from '@/components/form/fields/PhoneInput/PhoneInput';
import { Notification } from '@/components/Notification/Notification';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { DATE_TIME_FORMAT, formatDate } from '@/helpers/dateHelpers/dateHelpers';
import { getResponseError, prepareResponse } from '@/helpers/apiHelpers/responseHelpers';
import { openRecipientRequestApi } from '@/api/v1/recipient-requests/openRecipientRequestApi';
import { startRecipientRequestVerificationApi } from '@/api/v1/recipient-requests/startRecipientRequestVerificationApi';
import { getRecipientRequestVerificationStatusApi } from '@/api/v1/recipient-requests/getRecipientRequestVerificationStatusApi';
import { inviteRecipientApi } from '@/api/v1/recipient-requests/inviteRecipientApi';
import {
  submitRecipientProfileApi,
  SubmitRecipientProfilePayloadType,
} from '@/api/v1/recipient-requests/submitRecipientProfileApi';
import { OptionCard, OptionGrid } from '@/bundle/_OrgAdmin/pages/MyVaultPage/styles';
import { SecureLinkCard, SecureLinkShell } from '@/bundle/pages/SecureLinkPage/styles';
import { TemplateForm } from './ui/TemplateForm';
import { RecipientRequestVerificationInProgress } from './ui/RecipientRequestVerificationInProgress';
import { ID_VERIFICATION_POLLING_INTERVAL, isIdVerificationLiveStatus } from '@/helpers/verificationHelpers';
import { resendRecipientRequestVerificationApi } from '@/api/v1/recipient-requests/resendRecipientRequestVerificationApi';

const DEAD_MESSAGE = {
  expired: 'This request has expired. Ask the sender for a new one.',
  revoked: 'This request was revoked by the sender.',
  completed: 'Details were already submitted for this request.',
  closed: 'This request is closed.',
};

export const RecipientRequestPage = () => {
  const { token } = useParams();
  const [phone, setPhone] = useState('');
  const [filledBy, setFilledBy] = useState<'recipient' | 'proxy'>('recipient');
  const [inviteContact, setInviteContact] = useState('');
  const [invited, setInvited] = useState('');
  const [done, setDone] = useState<{ organization: string } | null>(null);
  const [error, setError] = useState('');
  const [showPhoneForm, setShowPhoneForm] = useState(false);

  const { data: openResponse, isPending } = useQuery({
    queryKey: ['recipient_request_open', token],
    queryFn: async () => prepareResponse(await openRecipientRequestApi(token)),
    retry: false,
  });
  const state = openResponse?.body;

  const { data: statusResponse, refetch: refetchStatus } = useQuery({
    queryKey: ['recipient_request_verify_status', token],
    queryFn: async () => prepareResponse(await getRecipientRequestVerificationStatusApi(token)),
    enabled: !!state?.requires_verification,
    // Polls while TrustStamp has a live session, on first load as well as after a reload.
    refetchInterval: (query) =>
      isIdVerificationLiveStatus(query.state.data?.body?.status) ? ID_VERIFICATION_POLLING_INTERVAL : false,
    refetchIntervalInBackground: true,
  });
  const verifyStatus = statusResponse?.body;

  const start = useMutation({
    mutationFn: async () => prepareResponse(await startRecipientRequestVerificationApi(token, phone)),
    onSuccess(response) {
      if (response.error) return setError(getResponseError(response.error));

      setError('');
      refetchStatus();
      setShowPhoneForm(false);
    },
  });

  const invite = useMutation({
    mutationFn: async () => prepareResponse(await inviteRecipientApi(token, inviteContact.trim())),
    onSuccess(response) {
      if (response.error) return setError(getResponseError(response.error));
      setInvited(response.body.contact);
      showSnackbar('The recipient has been invited.');
    },
  });

  const resend = useMutation({
    mutationKey: ['recipient_request_verify_resend', token],
    mutationFn: async () => prepareResponse(await resendRecipientRequestVerificationApi(token)),
    onSuccess(response) {
      if (response.error) return setError(getResponseError(response.error));
      setError('');
      showSnackbar('We sent a new verification link to your phone.');
      refetchStatus();
    },
  });

  const submit = useMutation({
    mutationFn: async (payload: SubmitRecipientProfilePayloadType) =>
      prepareResponse(await submitRecipientProfileApi(token, payload)),
    onSuccess(response) {
      if (response.error) return setError(getResponseError(response.error));
      setDone({ organization: response.body.organization });
    },
  });

  useEffect(() => {
    if (verifyStatus?.status === 'failed') {
      setError('Verification did not pass. You can try again.');
    }
  }, [verifyStatus?.status]);

  if (isPending) {
    return (
      <SecureLinkShell>
        <SecureLinkCard>
          <Typography>Loading...</Typography>
        </SecureLinkCard>
      </SecureLinkShell>
    );
  }

  if (!state || openResponse?.error) {
    return (
      <SecureLinkShell>
        <SecureLinkCard>
          <Typography variant='headline5'>This request cannot be opened.</Typography>
          <Typography variant='bodySmall'>{getResponseError(openResponse?.error) || 'The link is invalid.'}</Typography>
        </SecureLinkCard>
      </SecureLinkShell>
    );
  }

  const dead = DEAD_MESSAGE[state.state];
  const verified = !state.requires_verification || verifyStatus?.status === 'succeed';
  const inProgress = isIdVerificationLiveStatus(verifyStatus?.status);
  const resendIn = verifyStatus?.start_new_session_enable_after_sec ?? 0;
  const isProxyRequest = state.audience === 'proxy' && !state.opened_as_invited_recipient;

  return (
    <SecureLinkShell>
      <SecureLinkCard $maxWidth='800px'>
        <Typography variant='headline4' fontWeight='medium'>
          WireVault secure request
        </Typography>

        {done ? (
          <>
            <Typography variant='subTitle'>Details submitted</Typography>
            <Typography variant='bodySmall'>
              <b>{done.organization}</b> can now see your details in the order their bank asks for them. You will be
              notified if they request changes.
            </Typography>
          </>
        ) : dead ? (
          <>
            <Typography variant='subTitle'>Request unavailable</Typography>
            <Typography variant='bodySmall'>{dead}</Typography>
          </>
        ) : !verified ? (
          <>
            <Typography variant='subTitle'>Verify your identity</Typography>
            <Typography variant='bodySmall'>
              {state.organization} needs to know who is sending them bank details. Verification takes about two minutes:
              a government ID and a short liveness check through TrustStamp.
            </Typography>
            {error && <Notification variant='error'>{error}</Notification>}

            {(!inProgress || showPhoneForm) && (
              <Box display='flex' flexDirection='column' rowGap='12px'>
                <PhoneInput name='mobile_phone' label='Your mobile number' value={phone} onChange={setPhone} />
                <Typography variant='caption'>
                  TrustStamp will text you a link to complete the verification on your phone.
                </Typography>
                <ButtonNew size='medium' isLoading={start.isPending} disabled={!phone} onClick={() => start.mutate()}>
                  Start verification
                </ButtonNew>
              </Box>
            )}

            {inProgress && (
              <RecipientRequestVerificationInProgress
                key={verifyStatus.session_id ?? 'session'}
                phone={verifyStatus.phone_masked}
                isLoading={resend.isPending}
                canResend={verifyStatus.can_resend}
                onResend={() => resend.mutate()}
                onChangeNumber={() => setShowPhoneForm(true)}
                resendIn={resendIn}
                onTimerEnds={refetchStatus}
              />
            )}
            <Typography textAlign='right' variant='caption' fontWeight='medium'>
              Expires {formatDate(state.expires_at, DATE_TIME_FORMAT)}
            </Typography>
          </>
        ) : (
          <>
            <Box display='flex' justifyContent='space-between' alignItems='center'>
              <Typography variant='subTitle'>
                Your bank details for <b>{state.organization}</b>
              </Typography>
              {state.template_draft && (
                <Typography variant='caption'>{state.template_label} template (draft)</Typography>
              )}
            </Box>
            <Typography variant='bodySmall' fontWeight='medium'>
              {state.request_type === 'intl'
                ? 'These are the exact fields their bank asks for when they add you as an international recipient.'
                : `Only what a ${state.template_label} payment needs.`}
            </Typography>

            {isProxyRequest && (
              <>
                <OptionGrid $cols={2}>
                  <OptionCard type='button' $active={filledBy === 'recipient'} onClick={() => setFilledBy('recipient')}>
                    <Typography fontWeight='semibold'>I am the recipient</Typography>
                    <Typography variant='caption'>Fill in my own bank details below.</Typography>
                  </OptionCard>
                  <OptionCard type='button' $active={filledBy === 'proxy'} onClick={() => setFilledBy('proxy')}>
                    <Typography fontWeight='semibold'>I am a proxy</Typography>
                    <Typography variant='caption'>
                      Invite the recipient to fill it, or enter it on their behalf.
                    </Typography>
                  </OptionCard>
                </OptionGrid>
                {filledBy === 'proxy' && (
                  <Box display='flex' columnGap='20px' alignItems='center'>
                    <Input
                      name='invite'
                      label="Recipient's email or mobile"
                      value={inviteContact}
                      onChange={(event) => setInviteContact(event.target.value)}
                      placeholder='name@company.com or +46 ...'
                    />
                    <ButtonNew
                      variant='secondary'
                      isLoading={invite.isPending}
                      disabled={!inviteContact.trim() || !!invited}
                      onClick={() => invite.mutate()}
                    >
                      {invited ? `Invited ${invited}` : 'Invite recipient instead'}
                    </ButtonNew>
                  </Box>
                )}
              </>
            )}

            {error && <Notification variant='error'>{error}</Notification>}

            {!invited && (
              <TemplateForm
                fields={state.fields ?? []}
                filledBy={isProxyRequest ? filledBy : 'recipient'}
                wireConfirmation={!!state.wire_confirmation}
                isLoading={submit.isPending}
                apiError={submit.data?.error}
                onSubmit={(payload) => submit.mutate(payload)}
              />
            )}
            {invited && (
              <Typography variant='bodySmall'>
                The recipient will receive their own link. Nothing else is needed from you.
              </Typography>
            )}
          </>
        )}
      </SecureLinkCard>
    </SecureLinkShell>
  );
};
